import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCnpj, formatCpf, isValidCnpj, isValidCpf, stripNonDigits } from "../contacts/document-validation";
import { ROTULO_PAPEL, definirLotacao, useUnidades } from "../unidades/dados";
import { convidarPessoa, gestoresDaLista, idPeloEmail, type Gestor, type PessoaDaLista } from "./gestaoPessoas";

const NENHUM = "__nenhum__";
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatarDocumento = (v: string) => {
  const d = stripNonDigits(v).slice(0, 14);
  return d.length <= 11 ? formatCpf(d) : formatCnpj(d);
};

/**
 * Convite do administrador e do responsável pela unidade (migration 308):
 * nome, e-mail, documento, função, unidade e gerente. Comissão, salário e
 * credenciais continuam com o super admin.
 */
export const ConviteGestaoDialog = ({
  pessoas,
  papeis,
  roles,
  unidadeFixa,
  onClose,
  onInvited,
}: {
  pessoas: PessoaDaLista[];
  papeis: string[];
  roles: Array<{ id: string; code: string }>;
  /** Responsável pela unidade: convida só para a dele. */
  unidadeFixa: string | null;
  onClose: () => void;
  onInvited: (email: string) => void;
}) => {
  const { data: unidades } = useUnidades();
  const ativas = (unidades ?? []).filter((u) => u.ativa);
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [documento, setDocumento] = useState("");
  const [papel, setPapel] = useState(papeis.includes("user") ? "user" : papeis[0] ?? "");
  const [unidade, setUnidade] = useState<string>(unidadeFixa ?? NENHUM);
  const [gerente, setGerente] = useState<string>(NENHUM);
  const [gestores, setGestores] = useState<Gestor[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    void gestoresDaLista(pessoas).then(setGestores);
  }, [pessoas]);

  // Uma unidade ativa só: já vem escolhida.
  useEffect(() => {
    if (!unidadeFixa && unidade === NENHUM && ativas.length === 1) setUnidade(ativas[0].id);
  }, [ativas, unidade, unidadeFixa]);

  const gestoresDaUnidade = useMemo(
    () => gestores.filter((g) => unidade === NENHUM || g.unidadeId === unidade),
    [gestores, unidade],
  );

  const enviar = async () => {
    const doc = stripNonDigits(documento);
    const docValido = doc.length === 11 ? isValidCpf(doc) : doc.length === 14 ? isValidCnpj(doc) : false;
    if (!EMAIL.test(email.trim())) return setErro("E-mail inválido.");
    if (!docValido) return setErro("CPF ou CNPJ inválido.");
    const roleId = roles.find((r) => r.code === papel)?.id;
    if (!roleId) return setErro("Selecione a função.");
    if (unidade === NENHUM && ativas.length > 1) return setErro("Selecione a unidade.");

    setEnviando(true);
    setErro(null);
    try {
      const unidadeId = unidade === NENHUM ? null : unidade;
      await convidarPessoa({ email, nome, cpfCnpj: doc, roleId, unidadeId });
      if (gerente !== NENHUM) {
        const novoId = await idPeloEmail(email);
        if (novoId != null) await definirLotacao(novoId, unidadeId, Number(gerente));
      }
      onInvited(email.trim().toLowerCase());
      onClose();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível enviar o convite.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(aberto) => !aberto && onClose()}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Convidar pessoa</DialogTitle>
          <DialogDescription>Convite por e-mail para criar a senha de acesso.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="convite-nome">Nome</Label>
            <Input id="convite-nome" value={nome} onChange={(e) => setNome(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="convite-email">E-mail</Label>
            <Input id="convite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="convite-documento">CPF ou CNPJ</Label>
            <Input
              id="convite-documento"
              inputMode="numeric"
              value={documento}
              onChange={(e) => setDocumento(formatarDocumento(e.target.value))}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Função</Label>
            <Select value={papel} onValueChange={setPapel}>
              <SelectTrigger><SelectValue placeholder="Selecione a função" /></SelectTrigger>
              <SelectContent>
                {papeis.map((p) => (
                  <SelectItem key={p} value={p}>{ROTULO_PAPEL[p] ?? p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Unidade</Label>
            <Select value={unidade} onValueChange={(v) => { setUnidade(v); setGerente(NENHUM); }} disabled={!!unidadeFixa}>
              <SelectTrigger><SelectValue placeholder="Selecione a unidade" /></SelectTrigger>
              <SelectContent>
                {ativas.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Gerente</Label>
            <Select value={gerente} onValueChange={setGerente}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NENHUM}>Sem gerente</SelectItem>
                {gestoresDaUnidade.map((g) => (
                  <SelectItem key={g.crmId} value={String(g.crmId)}>{g.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {erro && <p className="text-sm text-destructive">{erro}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button className="bg-marinho" onClick={enviar} disabled={enviando || !nome.trim() || !email.trim()}>
            {enviando ? "Enviando..." : "Enviar convite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
